import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase/config';
import { createOrUpdateUser } from '../utils/userUtils';

// Super admin accounts that cannot be converted
const SUPER_ADMINS = [
  'work.goyalco@gmail.com',
  'avularudrasekharreddy@gmail.com'
];

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Sign up with email and password
  const signUp = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await updateProfile(result.user, {
        displayName: displayName
      });

      // Create user document in Firestore
      await createOrUpdateUser(result.user);
      
      return result;
    } catch (error) {
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password should be at least 6 characters long.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else {
        throw new Error(error.message || 'An error occurred during sign-up. Please try again.');
      }
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Store admin password temporarily for account creation (only for admins)
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (userDoc.exists() && (userDoc.data().role === 'admin' || userDoc.data().role === 'super_admin')) {
        localStorage.setItem('adminPassword', password);
      }
      
      return result;
    } catch (error) {
      // Handle specific Firebase auth errors
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled. Please contact support.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else {
        throw new Error(error.message || 'An error occurred during sign-in. Please try again.');
      }
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Create user document in Firestore if it doesn't exist
      await createUserDocument(result.user);
      
      return result;
    } catch (error) {
      // Handle specific Firebase auth errors
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up was blocked by your browser. Please allow pop-ups and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else {
        throw new Error(error.message || 'An error occurred during sign-in. Please try again.');
      }
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      // Clear stored admin password on logout
      localStorage.removeItem('adminPassword');
    } catch (error) {
      throw error;
    }
  };

  // Create user document in Firestore
  const createUserDocument = async (user, additionalData = {}) => {
    if (!user) return;
    
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      const { displayName, email, photoURL } = user;
      const createdAt = new Date();
      
      // Determine user role
      const role = SUPER_ADMINS.includes(email) ? 'super_admin' : 
                   additionalData.role || 'user';
      
      try {
        await setDoc(userRef, {
          displayName: displayName || additionalData.displayName || 'User',
          email,
          photoURL: photoURL || '',
          createdAt,
          selectedApp: null,
          role,
          ...additionalData
        });
      } catch (error) {
        console.error('Error creating user document:', error);
      }
    }
    
    return userRef;
  };

  // Get user profile from Firestore
  const getUserProfile = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  // Update user profile
  const updateUserProfile = async (userId, data) => {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, data, { merge: true });
      
      // Update local state
      if (userId === currentUser?.uid) {
        setUserProfile(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const [isCreatingUser, setIsCreatingUser] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Skip processing if we're in the middle of creating a user account
      if (isCreatingUser) {
        console.log('Skipping auth state change - creating user account');
        return;
      }
      
      if (user) {
        setCurrentUser(user);
        // Create or update user document in Firestore
        await createOrUpdateUser(user);
        // Get user profile from Firestore
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [isCreatingUser]);

  // Admin-only user creation
  const createUserByAdmin = async (email, password, displayName, role = 'user') => {
    try {
      // Check if current user is admin
      if (!isAdmin()) {
        throw new Error('Only admins can create user accounts.');
      }

      // Store current admin info
      const currentAdminUser = currentUser;
      const currentAdminProfile = userProfile;
      const currentAdminPassword = localStorage.getItem('adminPassword');

      if (!currentAdminPassword) {
        throw new Error('Admin session expired. Please sign in again to create accounts.');
      }

      // Set flag to prevent auth state changes from triggering side effects
      setIsCreatingUser(true);

      // Create the new user account
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await updateProfile(result.user, {
        displayName: displayName
      });

      // Create user document in Firestore with specified role
      await createUserDocument(result.user, { role });
      
      // Sign out the newly created user
      await signOut(auth);
      
      // Re-authenticate the admin user
      await signInWithEmailAndPassword(auth, currentAdminUser.email, currentAdminPassword);
      
      // Restore admin state immediately
      setCurrentUser(currentAdminUser);
      setUserProfile(currentAdminProfile);
      
      // Re-enable auth state changes
      setIsCreatingUser(false);
      
      return result;
    } catch (error) {
      // Re-enable auth state changes on error
      setIsCreatingUser(false);
      
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email is already registered.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password should be at least 6 characters long.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else {
        throw new Error(error.message || 'An error occurred during account creation.');
      }
    }
  };

  // Get all users (admin only for UI, but allow system functions)
  const getAllUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users = [];
      
      snapshot.forEach(doc => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  };

  // Get all users for admin UI only
  const getAllUsersForAdmin = async () => {
    try {
      if (!isAdmin()) {
        throw new Error('Only admins can view user list.');
      }
      return await getAllUsers();
    } catch (error) {
      console.error('Error getting users for admin:', error);
      throw error;
    }
  };

  // Update user role (admin only)
  const updateUserRole = async (userId, newRole) => {
    try {
      if (!isAdmin()) {
        throw new Error('Only admins can update user roles.');
      }

      // Get user data to check if they're a super admin
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Prevent changing super admin roles
        if (SUPER_ADMINS.includes(userData.email)) {
          throw new Error('Super admin roles cannot be changed.');
        }
        
        await updateDoc(userRef, { role: newRole });
      } else {
        throw new Error('User not found.');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  // Check if current user is admin
  const isAdmin = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  };

  // Check if current user is super admin
  const isSuperAdmin = () => {
    return userProfile?.role === 'super_admin';
  };

  const value = {
    currentUser,
    userProfile,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    updateUserProfile,
    createUserByAdmin,
    getAllUsers,
    getAllUsersForAdmin,
    updateUserRole,
    isAdmin,
    isSuperAdmin,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

